import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Mail, MessageCircle, Send, MapPin, Phone, Clock, ExternalLink } from 'lucide-react';
import { BackButton } from '@/components/ui/back-button';

export default function ContactPage() {
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Form submission logic would go here
    alert('Votre message a été envoyé. Nous vous répondrons dans les plus brefs délais.');
  };

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-6xl mx-auto space-y-8">
        <BackButton to="/" />
        
        <div className="text-center space-y-4 mb-8">
          <div className="inline-block p-3 bg-white rounded-2xl shadow-xl mb-4">
            <Mail className="w-10 h-10 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold">Contactez-nous</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Une question ? Un projet ? N'hésitez pas à nous contacter, nous vous répondrons dans les plus brefs délais.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <Card className="lg:col-span-2">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold mb-6">Envoyez-nous un message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="vous@exemple.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sujet
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Sélectionnez un sujet</option>
                    <option value="consultation">Demande de consultation</option>
                    <option value="information">Demande d'information</option>
                    <option value="partnership">Proposition de partenariat</option>
                    <option value="other">Autre</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <Textarea
                    required
                    className="min-h-[150px]"
                    placeholder="Décrivez votre demande en détail..."
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#54075B] hover:bg-[#54075B]/90 flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Envoyer le message
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardContent className="p-8 space-y-8">
              <h2 className="text-2xl font-bold mb-6">Informations de contact</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-purple-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold">Adresse</h3>
                    <p className="text-gray-600">
                      Cabinet Immersion 7<br />
                      77169 Boissy-le-Châtel<br />
                      France
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-purple-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold">Email</h3>
                    <a 
                      href="mailto:cabinet.immersion.7@gmail.com"
                      className="text-purple-600 hover:underline"
                    >
                      cabinet.immersion.7@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Phone className="w-6 h-6 text-purple-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold">Téléphone</h3>
                    <a 
                      href="tel:+33600000000"
                      className="text-purple-600 hover:underline"
                    >
                      +33 6 00 00 00 00
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Clock className="w-6 h-6 text-purple-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold">Horaires</h3>
                    <p className="text-gray-600">
                      Lundi - Vendredi: 9h00 - 18h00<br />
                      Samedi: 10h00 - 16h00<br />
                      Dimanche: Fermé
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <h3 className="font-semibold mb-4">Réservation</h3>
                <a 
                  href="https://www.resalib.fr/praticien/78473-cabinet-immersion-7-coach-en-developpement-personnel-boissy-le-chatel"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-purple-600 hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Prendre rendez-vous en ligne
                </a>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map */}
        <Card>
          <CardContent className="p-0 h-[400px] overflow-hidden rounded-lg">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d21039.080240767395!2d3.1151271!3d48.8242199!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e8a1e5f75b3e2f%3A0x40b82c3688c4b90!2s77169%20Boissy-le-Ch%C3%A2tel!5e0!3m2!1sfr!2sfr!4v1653384896542!5m2!1sfr!2sfr" 
              width="100%" 
              height="100%" 
              style={{ border: 0 }} 
              allowFullScreen 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="Cabinet Immersion 7 location"
            ></iframe>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}